<template>
  <PopoverRoot v-model:open="openState">
    <PopoverTrigger as-child>
      <a
        href="javascript:;"
        :class="resolvedTriggerClass"
      >
        <slot name="trigger">
          <span v-if="icon" v-html="icon" />
          <template v-else>{{ label }}</template>
        </slot>
      </a>
    </PopoverTrigger>
    <PopoverPortal :force-mount="true">
      <PopoverContent
        class="popover bs-popover-auto fade show tooltip-element"
        :side="rekaSide"
        :side-offset="6"
        :collision-padding="12"
      >
        <div v-if="title" class="popover-header">{{ title }}</div>
        <div class="popover-body" v-html="body" />
        <PopoverArrow class="popover-arrow" :width="12" :height="6" />
      </PopoverContent>
    </PopoverPortal>
  </PopoverRoot>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import {
  PopoverArrow,
  PopoverContent,
  PopoverPortal,
  PopoverRoot,
  PopoverTrigger,
} from 'reka-ui'

// Self-contained popover primitive built on Reka UI. Replaces the two legacy
// primitives this absorbs:
//   - PopupLink — text trigger that opens a Bootstrap popover via the
//     Rails-side `PopUpLink` global JS handler (forbidden Bootstrap JS).
//   - HelpIcon — icon trigger that did the same via the same handler.
//
// Why merge them: both are the same component shape (clickable trigger →
// popover with optional title + HTML body), differing only in what the
// trigger renders. Folding them into one primitive removes the duplication
// and gives consumers a single API for any popover.
//
// Trigger options, in priority order:
//   1. <template #trigger>...</template>  — fully custom (any markup)
//   2. `icon` prop (HTML string)          — renders the icon SVG via v-html;
//                                           styled with the .help-icon legacy
//                                           classes by default
//   3. `label` prop (text)                — renders the text label;
//                                           styled with the .popup-link
//                                           .dotted legacy classes by default
// `triggerClass` overrides the auto-assigned class. Consumers needing a
// non-anchor trigger element should use the `#trigger` slot.
//
// Trigger class on the anchor is `.popup-link` (not the legacy
// `.display-content`) to avoid double-firing when this primitive is used on a
// Slim page that also loads the Rails-side `PopUpLink` global handler
// (`document.arrive('.display-content')`).
//
// Behavioral simplification accepted in this port: the legacy API offered
// `popover` / `canvas` (offcanvas) / `modal` display modes per breakpoint.
// The Reka-based wrapper uses popover on all breakpoints. CMG's usages are
// short attribution / detail tooltips, for which popover-on-mobile is an
// acceptable UX. If a future consumer needs offcanvas-on-mobile, add a
// Reka Dialog-based branch then.
//
// Why `force-mount` on the portal: Reka's <PopoverPortal> wraps its <Teleport>
// in `v-if="isMounted || forceMount"`, where `isMounted` flips true in
// `onMounted`. The guard exists to avoid SSR hydration mismatches. We don't
// SSR (web-app/mobile-app are both client-rendered), so the guard adds an
// extra render tick for nothing — and on Node 20 jsdom that delay was enough
// to break CI tests asserting on the teleported content. `forceMount` skips
// the guard so the popover mounts immediately. Presence still gates rendering
// on the open state, so a closed popover still renders nothing.

const props = withDefaults(
  defineProps<{
    label?: string
    icon?: string
    title?: string
    body?: string
    direction?: 'auto' | 'top' | 'right' | 'bottom' | 'left'
    triggerClass?: string
    // Controlled `open` (v-model:open). When omitted, the popover is
    // uncontrolled and toggles internally on trigger click. Exposed mainly so
    // tests can drive the open state directly without going through Reka's
    // click → useVModel → Presence cascade, which is unreliable under jsdom.
    open?: boolean
  }>(),
  {
    label: '',
    icon: '',
    title: '',
    body: '',
    direction: 'auto',
    triggerClass: '',
    open: undefined,
  },
)

const emit = defineEmits<{ 'update:open': [value: boolean] }>()

// Bridge controlled `open` prop ↔ internal state. When the parent passes
// `v-model:open`, `openState` proxies to the prop and emits updates. When
// uncontrolled, it falls back to a local ref so trigger clicks still work.
const internalOpen = ref(false)
const openState = computed<boolean>({
  get: () => (props.open === undefined ? internalOpen.value : props.open),
  set: (value) => {
    if (props.open === undefined) internalOpen.value = value
    else emit('update:open', value)
  },
})

// Map the `direction` API to Reka's `side` prop. `'auto'` keeps Reka's default
// placement (top) with collision detection enabled, which is the closest
// equivalent to the legacy Popper-driven "auto" behavior.
const rekaSide = computed<'top' | 'right' | 'bottom' | 'left'>(() => {
  if (props.direction === 'auto') return 'top'
  return props.direction
})

// Default trigger styling depends on which slot variant is in use.
// `triggerClass` always wins when provided.
const resolvedTriggerClass = computed(() => {
  if (props.triggerClass) {
    return [props.triggerClass, { 'popup--active': openState.value }]
  }
  if (props.icon) {
    return ['help-icon', 'help-icon--blue', { 'popup--active': openState.value }]
  }
  return ['popup-link', 'dotted', { 'popup--active': openState.value }]
})
</script>
